/**
 * Gespeicherte Filter im Phase Board (#141) — reiner Kern ohne React und ohne Netz.
 *
 * Ein gespeicherter Filter ist ein benannter Schnappschuss der Board-Filter samt Linse und
 * Persona-Chip (ohne Freitextsuche). Persönlich, nur Phase Board. Er liegt NICHT in
 * `view_filters.board` (den gemerkten Filtern): dort ersetzt jeder Schnappschuss die ganze
 * Sicht, ein gespeicherter Filter würde beim nächsten Klick überschrieben.
 *
 * Begriff in der Oberfläche: „Gespeicherter Filter" / „Meine Filter" — „Sicht" ist in der PV
 * schon die Persona.
 */
import {
  type BoardFilters,
  NAME_GESPEICHERTER_FILTER_MAX,
  SAVED_BOARD_FILTERS_MAX,
  type SavedBoardFilter,
} from '@projektverwaltung/shared';
import { NO_TAGS_VALUE } from '../components/v2/filters/filter-primitives.js';

export type NamensFehler = 'name_leer' | 'name_zu_lang' | 'name_vergeben' | 'zu_viele';
/** Was der Namensdialog anzeigen kann: Regelverstoß oder gescheitertes Speichern (Netz/Server). */
export type DialogFehler = NamensFehler | 'speichern_fehlgeschlagen';

export type Ergebnis = { ok: true; liste: SavedBoardFilter[] } | { ok: false; grund: NamensFehler };

function pruefeName(
  liste: readonly SavedBoardFilter[],
  name: string,
  ausserId?: string,
): NamensFehler | null {
  const n = name.trim();
  if (n.length === 0) return 'name_leer';
  if (n.length > NAME_GESPEICHERTER_FILTER_MAX) return 'name_zu_lang';
  const klein = n.toLocaleLowerCase('de');
  if (liste.some((f) => f.id !== ausserId && f.name.trim().toLocaleLowerCase('de') === klein)) {
    return 'name_vergeben';
  }
  return null;
}

/** Den aktuellen Filterstand unter `name` ablegen (hinten anhängen). */
export function anlegen(
  liste: readonly SavedBoardFilter[],
  args: { id: string; name: string; filters: BoardFilters; jetzt: Date },
): Ergebnis {
  if (liste.length >= SAVED_BOARD_FILTERS_MAX) return { ok: false, grund: 'zu_viele' };
  const fehler = pruefeName(liste, args.name);
  if (fehler) return { ok: false, grund: fehler };
  return {
    ok: true,
    liste: [
      ...liste,
      {
        id: args.id,
        name: args.name.trim(),
        filters: args.filters,
        created_at: args.jetzt.toISOString(),
      },
    ],
  };
}

export function umbenennen(liste: readonly SavedBoardFilter[], id: string, name: string): Ergebnis {
  const fehler = pruefeName(liste, name, id);
  if (fehler) return { ok: false, grund: fehler };
  return { ok: true, liste: liste.map((f) => (f.id === id ? { ...f, name: name.trim() } : f)) };
}

/** „Mit aktuellem Stand überschreiben": Name und Position bleiben, die Filter wechseln. */
export function ueberschreiben(
  liste: readonly SavedBoardFilter[],
  id: string,
  filters: BoardFilters,
): SavedBoardFilter[] {
  return liste.map((f) => (f.id === id ? { ...f, filters } : f));
}

export function loeschen(liste: readonly SavedBoardFilter[], id: string): SavedBoardFilter[] {
  return liste.filter((f) => f.id !== id);
}

/**
 * Werte, die es nicht mehr gibt, beim Aufruf überspringen: gelöschte Tags, inaktive oder
 * unbekannte Personen (PL, Manager, MTB). `entfernt` zählt die übersprungenen Werte für den
 * Hinweis „1 Filterwert gibt es nicht mehr". Der gespeicherte Filter selbst bleibt unverändert
 * — wird die Person reaktiviert, greift er wieder vollständig.
 */
export function bereinige(
  filters: BoardFilters,
  bekannt: { userIds: ReadonlySet<string>; tagIds: ReadonlySet<string> },
): { filters: BoardFilters; entfernt: number } {
  let entfernt = 0;
  const out: BoardFilters = { ...filters };
  // Altbestand (#81): der Einzel-Tag ist nur Rollback-Brücke der gemerkten Filter.
  out.tag = undefined;

  if (out.pl) {
    const pl = out.pl.filter((id) => bekannt.userIds.has(id));
    entfernt += out.pl.length - pl.length;
    out.pl = pl;
  }
  if (out.tags) {
    const tags = out.tags.filter((id) => id === NO_TAGS_VALUE || bekannt.tagIds.has(id));
    entfernt += out.tags.length - tags.length;
    out.tags = tags;
  }
  if (out.manager && !bekannt.userIds.has(out.manager)) {
    out.manager = null;
    entfernt += 1;
  }
  if (
    out.mtb &&
    out.mtb !== 'assigned' &&
    out.mtb !== 'unassigned' &&
    !bekannt.userIds.has(out.mtb)
  ) {
    out.mtb = null;
    entfernt += 1;
  }
  return { filters: out, entfernt };
}
